import React, { useState } from "react";
import PageShell from "@/components/PageShell";
import { toast } from "sonner";
import { Mail, Instagram, MapPin } from "lucide-react";

const Contact = () => {
    const [form, setForm] = useState({ name: "", email: "", subject: "general", message: "" });
    const [sending, setSending] = useState(false);

    const submit = (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Fill every field.");
            return;
        }
        setSending(true);
        // Front-end only confirmation - hooks to a real backend mailer can be added later
        setTimeout(() => {
            setSending(false);
            toast.success("Message locked in. We'll reply within 48h.");
            setForm({ name: "", email: "", subject: "general", message: "" });
        }, 800);
    };

    const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    return (
        <PageShell
            kicker="[Direct Line]"
            title="Talk"
            accent="To The House."
            intro="Wholesale, press, pre-orders, lost packages, deep questions about gauge counts. We answer everything within 48 hours."
            testid="contact-page"
        >
            <section className="grid grid-cols-1 lg:grid-cols-12 border-b-4 border-ink">
                <div className="lg:col-span-7 p-6 md:p-12 lg:border-r-4 border-ink">
                    <form onSubmit={submit} className="space-y-5">
                        <Field label="Name">
                            <input
                                value={form.name}
                                onChange={change("name")}
                                type="text"
                                required
                                placeholder="Your full name"
                                className="w-full border-2 border-ink px-4 py-3 font-mono-ui text-sm focus:outline-none focus:border-hazard"
                                data-testid="contact-name-input"
                            />
                        </Field>
                        <Field label="Email">
                            <input
                                value={form.email}
                                onChange={change("email")}
                                type="email"
                                required
                                placeholder="you@domain.com"
                                className="w-full border-2 border-ink px-4 py-3 font-mono-ui text-sm focus:outline-none focus:border-hazard"
                                data-testid="contact-email-input"
                            />
                        </Field>
                        <Field label="Subject">
                            <select
                                value={form.subject}
                                onChange={change("subject")}
                                className="w-full border-2 border-ink px-4 py-3 font-mono-ui text-sm bg-background focus:outline-none focus:border-hazard"
                                data-testid="contact-subject-select"
                            >
                                <option value="general">General Inquiry</option>
                                <option value="order">Order Issue</option>
                                <option value="wholesale">Wholesale / Stockist</option>
                                <option value="press">Press</option>
                                <option value="returns">Returns & Exchanges</option>
                            </select>
                        </Field>
                        <Field label="Message">
                            <textarea
                                value={form.message}
                                onChange={change("message")}
                                required
                                rows={6}
                                placeholder="Say what you need to say."
                                className="w-full border-2 border-ink px-4 py-3 font-mono-ui text-sm resize-none focus:outline-none focus:border-hazard"
                                data-testid="contact-message-input"
                            />
                        </Field>
                        <button
                            type="submit"
                            disabled={sending}
                            className="w-full bg-ink text-black border-2 border-ink py-4 font-mono-ui text-xs uppercase tracking-[0.3em] hover:bg-hazard hover:border-hazard disabled:opacity-50"
                            data-testid="contact-submit-button"
                        >
                            {sending ? "Sending…" : "Send Message →"}
                        </button>
                    </form>
                </div>

                <aside className="lg:col-span-5 p-6 md:p-12 bg-bone space-y-8">
                    <ContactBlock
                        Icon={Mail}
                        label="Email"
                        primary="hello@uknitwear.com"
                        secondary="wholesale@uknitwear.com / press@uknitwear.com"
                    />
                    <ContactBlock
                        Icon={MapPin}
                        label="Flagship / HQ"
                        primary="London"
                        secondary={"65 Brushfield St\nLondon, United Kingdom\nE1 6AA"}
                    />
                    <ContactBlock
                        Icon={Instagram}
                        label="Follow"
                        primary="@uknitwear"
                        secondary="Drop alerts, lookbook teasers, BTS."
                    />
                    <div className="border-t-2 border-ink pt-6">
                        <h4 className="font-mono-ui text-xs uppercase tracking-[0.25em] mb-3">
                            // Hours
                        </h4>
                        <ul className="font-mono-ui text-xs space-y-1">
                            <li>Mon — Fri / 10:00 — 18:00 GMT</li>
                            <li>Sat / 11:00 — 16:00 GMT</li>
                            <li className="text-muted-foreground">Sun / Closed</li>
                        </ul>
                    </div>
                </aside>
            </section>
        </PageShell>
    );
};

const Field = ({ label, children }) => (
    <div>
        <label className="block font-mono-ui text-[10px] uppercase tracking-[0.3em] mb-2">
            {label}
        </label>
        {children}
    </div>
);

const ContactBlock = ({ Icon, label, primary, secondary }) => (
    <div>
        <div className="flex items-center gap-2 mb-2">
            <Icon className="w-4 h-4" />
            <h4 className="font-mono-ui text-xs uppercase tracking-[0.25em]">{label}</h4>
        </div>
        <p className="font-heading text-2xl uppercase leading-none">{primary}</p>
        <p className="mt-1 font-mono-ui text-xs text-muted-foreground whitespace-pre-line">
            {secondary}
        </p>
    </div>
);

export default Contact;
